import Cookies from "js-cookie";
import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../../Components/Navbar";
import { fetchProfile } from "../../utils/fetchProfile";
import { DataGrid } from "@mui/x-data-grid";
import { useSearchParams, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { Tooltip, ImageList, ImageListItem } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import { CircularProgress } from "@mui/material";
import { fetchStorePhotoBySurveyIdStoreCode } from "../../services/storeMasterService";
import { ModalViewEditTrx } from "./Modal/ModalViewEditTrx";
import { PhotoLightbox } from "../../Components/PhotoLightBox";
import JSZip from "jszip";
import { Download } from "@mui/icons-material";

function ViewDataTrx() {
  const token = Cookies.get("access_token") || "";
  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [dataProfile, setDataProfile] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);

  const [modalTableData, setModalTableData] = useState([]);
  const [auditPlan, setAuditPlan] = useState(null);
  const [storeCode, setStoreCode] = useState(null);
  const [statusCode, setStatusCode] = useState("");
  const [surveyType, setSurveyType] = useState("");
  const [selectedProductCode, setSelectedProductCode] = useState("");
  const [storePhotoDetail, setStorePhotoDetail] = useState([]);

  const [modalOpenEdit, setModalOpenEdit] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // ZIP download state
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadZip = async () => {
    setIsDownloading(true);
    try {
      const zip = new JSZip();

      await Promise.all(
        storePhotoDetail.map(async (photo, i) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = photo.file;

          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");

          ctx.drawImage(img, 0, 0);

          const w = canvas.width;
          const padding = 10;
          const lineHeight = Math.max(14, Math.floor(w * 0.022));

          const lines = [
            `${auditPlan?.store_code} · ${auditPlan?.store_type}`,
            auditPlan?.store_name || "",
            auditPlan?.audit_plan || "",
            `Surveyor: ${auditPlan?.user_full_name_surveyor} (${auditPlan?.user_name_surveyor})`,
            `${auditPlan?.audit_start_at} – ${auditPlan?.audit_end_at} · ${auditPlan?.audit_status}`,
          ].filter(Boolean);

          const boxHeight = lines.length * (lineHeight + 4) + padding * 2;

          ctx.fillStyle = "rgba(0, 0, 0, 0.72)";
          ctx.fillRect(0, 0, w, boxHeight);

          ctx.textBaseline = "top";
          lines.forEach((line, j) => {
            const y = padding + j * (lineHeight + 4);
            if (j === 0) {
              ctx.fillStyle = "#0ab9cc";
              ctx.font = `bold ${lineHeight}px monospace`;
            } else if (j === 1) {
              ctx.fillStyle = "rgba(255,255,255,0.95)";
              ctx.font = `500 ${lineHeight}px sans-serif`;
            } else {
              ctx.fillStyle = "rgba(255,255,255,0.65)";
              ctx.font = `${lineHeight - 1}px sans-serif`;
            }
            ctx.fillText(line, padding, y);
          });

          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.92)
          );

          zip.file(`foto_${i + 1}_${auditPlan?.store_name}.jpg`, blob);
        })
      );

      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${storeCode}_${auditPlan?.store_name}_${auditPlan?.audit_plan}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("ZIP download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const [searchParams] = useSearchParams();
  const isQc = searchParams.get("is_qc");

  const { surveyId, storeId } = useParams();

  useEffect(() => {
    fetchProfile(token, setDataProfile, setNotAllowAccessPage, []);
  }, [token]);

  const fetchAuditDetail = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/audit-plan/${storeId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        setAuditPlan(data.data);
        setStoreCode(data.data.store_code);
        setStatusCode(data.data.audit_status);
      } else {
        console.error("Failed to fetch audit detail:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [token, storeId]);

  useEffect(() => {
    fetchAuditDetail();
  }, [fetchAuditDetail]);

  const fetchViewTrx = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/trx/rawdata/${surveyId}/${storeCode}?is_qc=${isQc}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        setModalTableData(data.data);
        setSurveyType(data.detail.survey_type.toUpperCase());
      } else {
        console.error("Failed to fetch data:", response.statusText);
        setModalTableData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setModalTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, surveyId, storeCode, isQc]);

  const handleViewPhoto = useCallback(async () => {
    setStorePhotoDetail(
      await fetchStorePhotoBySurveyIdStoreCode(
        token,
        surveyId,
        storeCode,
        setIsPhotoLoading
      )
    );
  }, [token, surveyId, storeCode]);

  useEffect(() => {
    setIsPhotoLoading(true);
    if (storeCode) {
      handleViewPhoto();
      fetchViewTrx();
    }
  }, [storeCode, handleViewPhoto, fetchViewTrx]);

  const [columnsView, setColumnsView] = useState([]);

  useEffect(() => {
    if (modalTableData.length > 0) {
      const dynamicColumns = Object.keys(modalTableData[0])
        .filter((key) => key !== "id")
        .map((key) => ({
          field: key,
          headerClassName: "super-app-theme--header",
          headerName: key.toUpperCase(),
          width: 200,
          align: "left",
          headerAlign: "center",
          editable: false,
        }));

      const userRole = dataProfile.role_code;

      if (
        statusCode === "Closed" &&
        userRole !== "TL" &&
        surveyId !== "65cc7b99e0747712517ed5e3" &&
        isQc !== "true" &&
        surveyType !== "RAK1"
      ) {
        dynamicColumns.push({
          field: "actions",
          headerClassName: "super-app-theme--header",
          headerName: "ACTIONS",
          headerAlign: "center",
          align: "center",
          width: 200,
          renderCell: (params) => {
            const { store_code, product_code } = params.row;

            const handleViewDataEdit = () => {
              openModalEdit(store_code, product_code);
            };

            return (
              <Tooltip title="View Data Edit" onClick={handleViewDataEdit}>
                <IconButton>
                  <BackupTableIcon />
                </IconButton>
              </Tooltip>
            );
          },
        });
      }

      setColumnsView(dynamicColumns);
    }
  }, [modalTableData, statusCode, dataProfile, surveyId, isQc, surveyType]);

  const openModalEdit = (storeCode, productCode) => {
    setModalOpenEdit(true);
    setSelectedProductCode(productCode);
  };

  const handlePhotoClick = (clickedIndex) => {
    setLightboxIndex(clickedIndex);
    setLightboxOpen(true);
  };

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Data Survey"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }

  return (
    <div>
      <Navbar
        active="Data Survey"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div className="content" style={{ padding: "1rem" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 500,
            }}
          >
            <CircularProgress />
          </Box>
        ) : modalTableData.length > 0 ? (
          <div style={{ marginTop: "20px" }}>
            <div style={{ marginLeft: "20px", fontSize: "18px" }}>
              <p>Project: {auditPlan.audit_plan}</p>
              <p>Nama Toko: {auditPlan.store_name}</p>
            </div>
            <div style={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={modalTableData}
                columns={columnsView}
                getRowId={(row) => row.id}
                sx={{
                  mt: 2,
                  ml: 2,
                  mr: 2,
                  "& .super-app-theme--header": {
                    backgroundImage: "linear-gradient(#0ab9cc, #069dae)",
                    color: "#FFF",
                    border: 1,
                    fontWeight: "bold",
                    borderColor: "rgb(234,243,248)",
                  },
                  "& .super-app-theme--header .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.2,
                    textAlign: "center",
                  },
                  borderRadius: "10px",
                  fontSize: 15,
                }}
                getRowHeight={() => "auto"}
              />
            </div>
          </div>
        ) : (
          <div>
            <p style={{ marginLeft: "20px" }}>Data Kosong</p>
          </div>
        )}

        {/* Photo section */}
        {isPhotoLoading ? (
          <p style={{ marginLeft: "20px" }}>Memuat foto...</p>
        ) : storePhotoDetail?.length > 0 ? (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 8,
              }}
            >
              <div style={{ marginLeft: -8 }}>
                <Tooltip title="Download semua foto (ZIP)" arrow>
                  <span>
                    <IconButton
                      onClick={handleDownloadZip}
                      disabled={isDownloading}
                      sx={{
                        borderRadius: 999,
                        mt: 1,
                        px: 2,
                        py: 1,
                        backgroundColor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Download />
                      )}
                      <span style={{ fontSize: 14, fontWeight: 500 }}>
                        {isDownloading ? "Mengunduh..." : "Download all photo (ZIP)"}
                      </span>
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            <ImageList variant="masonry" cols={5} gap={8}>
              {storePhotoDetail.map((item, i) => (
                <ImageListItem
                  key={item.id}
                  onClick={() => handlePhotoClick(i)}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    "&:hover img": { opacity: 0.75 },
                  }}
                >
                  <img
                    src={item.file}
                    alt={`Store ${i + 1}`}
                    loading="lazy"
                    style={{
                      transition: "opacity 0.2s",
                      display: "block",
                      width: "100%",
                    }}
                  />

                  {/* Info overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      background: "rgba(0,0,0,0.72)",
                      padding: "6px 8px",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        color: "#0ab9cc",
                        fontFamily: "monospace",
                        fontSize: 10,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {auditPlan?.store_code} · {auditPlan?.store_type}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 10,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {auditPlan?.store_name}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 9,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {i + 1}/{storePhotoDetail.length} · {auditPlan?.audit_start_at}{" "}
                      – {auditPlan?.audit_end_at}
                    </div>
                    <div
                      style={{
                        display: "inline-block",
                        marginTop: 3,
                        background:
                          auditPlan?.audit_status === "Closed"
                            ? "rgba(234,75,74,0.25)"
                            : "rgba(10,185,204,0.25)",
                        color:
                          auditPlan?.audit_status === "Closed"
                            ? "#f09595"
                            : "#0ab9cc",
                        fontSize: 9,
                        padding: "1px 5px",
                        borderRadius: 3,
                        fontFamily: "monospace",
                      }}
                    >
                      {auditPlan?.audit_status}
                    </div>
                  </div>
                </ImageListItem>
              ))}
            </ImageList>
          </div>
        ) : (
          <p style={{ marginLeft: "20px" }}>Foto tidak ditemukan</p>
        )}

        {/* Lightbox */}
        <PhotoLightbox
          photos={storePhotoDetail}
          open={lightboxOpen}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          auditPlan={auditPlan}
        />

        <ModalViewEditTrx
          surveyId={surveyId}
          storeCode={storeCode}
          selectedProductCode={selectedProductCode}
          open={modalOpenEdit}
          onClose={() => setModalOpenEdit(false)}
        />
      </div>
    </div>
  );
}

export default ViewDataTrx;